/**
 * Dates.
 *
 * Calendar dates are `YYYY-MM-DD` strings, never Date or Timestamp (SD-01).
 * A transaction dated 3 Aug 2026 is dated 3 Aug 2026 regardless of the device's
 * timezone. `YYYY-MM-DD` also sorts lexicographically in chronological order, so
 * Firestore range queries work directly on it.
 *
 * The financial year start month is configurable and defaults to April
 * (`settings/app.financialYearStartMonth`) — nothing here hardcodes it.
 */

export type DateKey = string; // YYYY-MM-DD
export type MonthKey = string; // YYYY-MM
export type FyKey = string; // YYYY-YY, e.g. '2026-27'

export const DEFAULT_FY_START_MONTH = 4; // April

export class DateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DateError';
  }
}

const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_KEY_PATTERN = /^(\d{4})-(\d{2})$/;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function isValidDateKey(value: unknown): value is DateKey {
  if (typeof value !== 'string') return false;
  const match = DATE_KEY_PATTERN.exec(value);
  if (!match) return false;

  const [, y, m, d] = match;
  const year = Number(y);
  const month = Number(m);
  const day = Number(d);

  if (month < 1 || month > 12) return false;
  if (day < 1 || day > daysInMonth(year, month)) return false;
  return true;
}

export function assertDateKey(value: unknown, label = 'date'): asserts value is DateKey {
  if (!isValidDateKey(value)) {
    throw new DateError(`${label} must be a valid YYYY-MM-DD string, got ${String(value)}`);
  }
}

export function isValidMonthKey(value: unknown): value is MonthKey {
  if (typeof value !== 'string') return false;
  const match = MONTH_KEY_PATTERN.exec(value);
  if (!match) return false;
  const month = Number(match[2]);
  return month >= 1 && month <= 12;
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

export function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

const pad = (n: number, width = 2): string => String(n).padStart(width, '0');

/** Build a DateKey from a Date using its *local* components, not UTC. */
export function toDateKey(date: Date): DateKey {
  if (Number.isNaN(date.getTime())) throw new DateError('invalid Date');
  return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function todayKey(now: Date = new Date()): DateKey {
  return toDateKey(now);
}

export function makeDateKey(year: number, month: number, day: number): DateKey {
  const key = `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
  assertDateKey(key);
  return key;
}

/** Parse to a Date at *local* midnight, for the rare case a Date is genuinely needed. */
export function dateKeyToDate(dateKey: DateKey): Date {
  assertDateKey(dateKey);
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function partsOf(dateKey: DateKey): { year: number; month: number; day: number } {
  assertDateKey(dateKey);
  const [y, m, d] = dateKey.split('-').map(Number);
  return { year: y, month: m, day: d };
}

// ---------------------------------------------------------------------------
// Derived keys (SD-06)
// ---------------------------------------------------------------------------

/** `monthKey` is a prefix of `dateKey`, so the two can never disagree. */
export function monthKeyOf(dateKey: DateKey): MonthKey {
  assertDateKey(dateKey);
  return dateKey.slice(0, 7);
}

/**
 * The financial year a date falls in, as `YYYY-YY`.
 *
 * With an April start: Aug 2026 is FY 2026-27; Feb 2026 is FY 2025-26.
 */
export function fyKeyOf(dateKey: DateKey, fyStartMonth = DEFAULT_FY_START_MONTH): FyKey {
  const { year, month } = partsOf(dateKey);
  const startYear = month >= fyStartMonth ? year : year - 1;
  return `${pad(startYear, 4)}-${pad((startYear + 1) % 100)}`;
}

export function fyKeyOfMonth(monthKey: MonthKey, fyStartMonth = DEFAULT_FY_START_MONTH): FyKey {
  return fyKeyOf(`${monthKey}-01`, fyStartMonth);
}

// ---------------------------------------------------------------------------
// Ranges
// ---------------------------------------------------------------------------

export interface DateRange {
  start: DateKey;
  end: DateKey;
}

export function monthRange(monthKey: MonthKey): DateRange {
  if (!isValidMonthKey(monthKey)) {
    throw new DateError(`invalid month key: ${String(monthKey)}`);
  }
  const [year, month] = monthKey.split('-').map(Number);
  return {
    start: `${monthKey}-01`,
    end: `${monthKey}-${pad(daysInMonth(year, month))}`,
  };
}

export function calendarYearRange(year: number): DateRange {
  return { start: `${pad(year, 4)}-01-01`, end: `${pad(year, 4)}-12-31` };
}

export function fyRange(fyKey: FyKey, fyStartMonth = DEFAULT_FY_START_MONTH): DateRange {
  const startYear = Number(fyKey.slice(0, 4));
  if (!Number.isInteger(startYear)) throw new DateError(`invalid FY key: ${fyKey}`);

  const endYear = fyStartMonth === 1 ? startYear : startYear + 1;
  const endMonth = fyStartMonth === 1 ? 12 : fyStartMonth - 1;

  return {
    start: `${pad(startYear, 4)}-${pad(fyStartMonth)}-01`,
    end: `${pad(endYear, 4)}-${pad(endMonth)}-${pad(daysInMonth(endYear, endMonth))}`,
  };
}

export function isWithin(dateKey: DateKey, range: DateRange): boolean {
  assertDateKey(dateKey);
  return dateKey >= range.start && dateKey <= range.end;
}

// ---------------------------------------------------------------------------
// Month arithmetic
// ---------------------------------------------------------------------------

export function addMonths(monthKey: MonthKey, count: number): MonthKey {
  if (!isValidMonthKey(monthKey)) {
    throw new DateError(`invalid month key: ${String(monthKey)}`);
  }
  const [year, month] = monthKey.split('-').map(Number);
  const zeroBased = year * 12 + (month - 1) + count;
  return `${pad(Math.floor(zeroBased / 12), 4)}-${pad((zeroBased % 12) + 1)}`;
}

/** Inclusive list of month keys from `from` to `to`. */
export function monthsBetween(from: MonthKey, to: MonthKey): MonthKey[] {
  if (!isValidMonthKey(from) || !isValidMonthKey(to)) {
    throw new DateError(`invalid month key: ${String(from)} / ${String(to)}`);
  }
  if (from > to) return [];

  const months: MonthKey[] = [];
  for (let key = from; key <= to; key = addMonths(key, 1)) months.push(key);
  return months;
}

export function monthsOfFy(fyKey: FyKey, fyStartMonth = DEFAULT_FY_START_MONTH): MonthKey[] {
  const { start, end } = fyRange(fyKey, fyStartMonth);
  return monthsBetween(start.slice(0, 7), end.slice(0, 7));
}

/**
 * Add days, handling month and year boundaries. Used for due dates and the 30-day
 * trash window.
 */
export function addDays(dateKey: DateKey, count: number): DateKey {
  const date = dateKeyToDate(dateKey);
  date.setDate(date.getDate() + count);
  return toDateKey(date);
}

export function daysBetween(from: DateKey, to: DateKey): number {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const diff = dateKeyToDate(to).getTime() - dateKeyToDate(from).getTime();
  return Math.round(diff / MS_PER_DAY);
}

/** Whole months elapsed — used by the loan foreclosure rules (FR-LOAN-07). */
export function monthsElapsed(from: DateKey, to: DateKey): number {
  const a = partsOf(from);
  const b = partsOf(to);
  const months = (b.year - a.year) * 12 + (b.month - a.month);
  return b.day >= a.day ? months : months - 1;
}

// ---------------------------------------------------------------------------
// Display (FR-SET-01 — dd-mm-yyyy, never the workbook's mm-dd-yy)
// ---------------------------------------------------------------------------

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export type DateDisplayFormat = 'dd-mm-yyyy' | 'dd MMM yyyy' | 'dd MMM' | 'yyyy-mm-dd';

export function formatDateKey(dateKey: DateKey, format: DateDisplayFormat = 'dd-mm-yyyy'): string {
  const { year, month, day } = partsOf(dateKey);
  switch (format) {
    case 'dd-mm-yyyy':
      return `${pad(day)}-${pad(month)}-${year}`;
    case 'dd MMM yyyy':
      return `${pad(day)} ${MONTH_NAMES[month - 1]} ${year}`;
    case 'dd MMM':
      return `${pad(day)} ${MONTH_NAMES[month - 1]}`;
    case 'yyyy-mm-dd':
      return dateKey;
  }
}

export function formatMonthKey(monthKey: MonthKey): string {
  if (!isValidMonthKey(monthKey)) {
    throw new DateError(`invalid month key: ${String(monthKey)}`);
  }
  const [year, month] = monthKey.split('-').map(Number);
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

export function formatFyKey(fyKey: FyKey): string {
  return `FY ${fyKey}`;
}

/**
 * Parse dd-mm-yyyy or dd/mm/yyyy typed by the user. Returns null rather than
 * throwing, so the form can show a message instead of crashing.
 */
export function parseUserDate(input: string): DateKey | null {
  const match = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(input.trim());
  if (!match) return null;

  const key = makeDateKeySafe(Number(match[3]), Number(match[2]), Number(match[1]));
  return key;
}

function makeDateKeySafe(year: number, month: number, day: number): DateKey | null {
  const key = `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
  return isValidDateKey(key) ? key : null;
}
